import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fileUploadApi, FileUploadPayload, ExistingDocument } from '@/services/file-upload';
import { FileUploadResponse, UpdateDocumentsRequest, UpdateDocumentsResponse } from '@/types/api';

// Hook for fetching existing onboarding documents
export const useExistingOnboardingDocuments = (
    organizationId: string | null,
    accountType: 'architect' | 'subcontractor'
) => {
    return useQuery<ExistingDocument[], Error>({
        queryKey: ['onboarding-documents', organizationId, accountType],
        queryFn: () => {
            if (!organizationId) {
                return Promise.resolve([]);
            }
            if (accountType === 'architect') {
                return fileUploadApi.getArchitectOnboardingDocuments(organizationId);
            } else {
                return fileUploadApi.getSubcontractorOnboardingDocuments(organizationId);
            }
        },
        enabled: !!organizationId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Hook for uploading files during onboarding
export const useUploadOnboardingFile = (accountType: 'architect' | 'subcontractor') => {
    const queryClient = useQueryClient();

    return useMutation<FileUploadResponse, Error, FileUploadPayload>({
        mutationFn: (payload: FileUploadPayload) => {
            if (accountType === 'architect') {
                return fileUploadApi.uploadArchitectFile(payload);
            } else {
                return fileUploadApi.uploadSubcontractorFile(payload);
            }
        },
        onSuccess: (_, variables) => {
            // Invalidate the existing documents query to refresh the list
            queryClient.invalidateQueries({
                queryKey: ['onboarding-documents', variables.organizationId, accountType]
            });
        },
        onError: (error) => {
            console.error('Failed to upload file:', error);
        },
    });
};

// Hook for updating architect documents
export const useUpdateArchitectDocuments = () => {
    const queryClient = useQueryClient();

    return useMutation<UpdateDocumentsResponse, Error, { architectId: string; data: UpdateDocumentsRequest }>({
        mutationFn: ({ architectId, data }) => fileUploadApi.updateArchitectDocuments(architectId, data),
        onSuccess: (data, variables) => {
            console.log('Architect documents updated successfully:', data);
            // Invalidate and refetch architect data
            queryClient.invalidateQueries({ queryKey: ['architect', variables.architectId] });
        },
        onError: (error) => {
            console.error('Failed to update architect documents:', error);
        },
    });
};

// Hook for updating subcontractor documents
export const useUpdateSubcontractorDocuments = () => {
    const queryClient = useQueryClient();

    return useMutation<UpdateDocumentsResponse, Error, { subcontractorId: string; data: UpdateDocumentsRequest }>({
        mutationFn: ({ subcontractorId, data }) => fileUploadApi.updateSubcontractorDocuments(subcontractorId, data),
        onSuccess: (data, variables) => {
            console.log('Subcontractor documents updated successfully:', data);
            // Invalidate and refetch subcontractor data
            queryClient.invalidateQueries({ queryKey: ['subcontractor', variables.subcontractorId] });
        },
        onError: (error) => {
            console.error('Failed to update subcontractor documents:', error);
        },
    });
};

// Hook for deleting files
export const useDeleteFile = (organizationId: string | null, accountType: 'architect' | 'subcontractor') => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: (fileId: string) => fileUploadApi.deleteFile(fileId),
        onSuccess: () => {
            console.log('File deleted successfully');
            // Invalidate the existing documents query to refresh the list
            if (organizationId) {
                queryClient.invalidateQueries({
                    queryKey: ['onboarding-documents', organizationId, accountType]
                });
            }
        },
        onError: (error) => {
            console.error('Failed to delete file:', error);
        },
    });
};
